/**
 * Country utilities for Axon Platform
 * Centralized country code to name/currency mapping
 * USA-first MVP strategy: US is primary market
 *
 * DRY: Single source of truth for country mappings
 */

// ISO 3166-1 alpha-2 country codes to names (English)
const COUNTRY_NAMES: Record<string, string> = {
  // Primary Markets
  US: 'United States',
  TR: 'Turkey',

  // European Union
  DE: 'Germany',
  FR: 'France',
  IT: 'Italy',
  ES: 'Spain',
  PL: 'Poland',
  NL: 'Netherlands',
  BE: 'Belgium',
  AT: 'Austria',
  RO: 'Romania',
  BG: 'Bulgaria',
  GR: 'Greece',
  HU: 'Hungary',
  CZ: 'Czech Republic',
  SK: 'Slovakia',
  PT: 'Portugal',
  SE: 'Sweden',
  DK: 'Denmark',
  FI: 'Finland',
  IE: 'Ireland',

  // Other Europe
  GB: 'United Kingdom',
  CH: 'Switzerland',
  NO: 'Norway',

  // North America
  CA: 'Canada',
  MX: 'Mexico',
};

// Turkish country names
const COUNTRY_NAMES_TR: Record<string, string> = {
  US: 'Amerika Birleşik Devletleri',
  TR: 'Türkiye',
  DE: 'Almanya',
  FR: 'Fransa',
  IT: 'İtalya',
  ES: 'İspanya',
  PL: 'Polonya',
  NL: 'Hollanda',
  BE: 'Belçika',
  AT: 'Avusturya',
  RO: 'Romanya',
  BG: 'Bulgaristan',
  GR: 'Yunanistan',
  HU: 'Macaristan',
  CZ: 'Çek Cumhuriyeti',
  SK: 'Slovakya',
  PT: 'Portekiz',
  SE: 'İsveç',
  DK: 'Danimarka',
  FI: 'Finlandiya',
  IE: 'İrlanda',
  GB: 'Birleşik Krallık',
  CH: 'İsviçre',
  NO: 'Norveç',
  CA: 'Kanada',
  MX: 'Meksika',
};

// Reverse mapping: country names to codes (case-insensitive lookup)
const NAME_TO_CODE: Record<string, string> = Object.entries(COUNTRY_NAMES).reduce(
  (acc, [code, name]) => {
    acc[name.toLowerCase()] = code;
    return acc;
  },
  {} as Record<string, string>
);

// ISO 3166-1 alpha-3 to alpha-2 mapping (for HERE Maps API compatibility)
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  USA: 'US',
  TUR: 'TR',
  DEU: 'DE',
  FRA: 'FR',
  ITA: 'IT',
  ESP: 'ES',
  POL: 'PL',
  NLD: 'NL',
  BEL: 'BE',
  AUT: 'AT',
  ROU: 'RO',
  BGR: 'BG',
  GRC: 'GR',
  HUN: 'HU',
  CZE: 'CZ',
  SVK: 'SK',
  PRT: 'PT',
  SWE: 'SE',
  DNK: 'DK',
  FIN: 'FI',
  IRL: 'IE',
  GBR: 'GB',
  CHE: 'CH',
  NOR: 'NO',
  CAN: 'CA',
  MEX: 'MX',
};

// ISO 3166-1 alpha-2 to alpha-3 mapping (for HERE Maps Autosuggest API)
const ALPHA2_TO_ALPHA3: Record<string, string> = Object.entries(ALPHA3_TO_ALPHA2).reduce(
  (acc, [alpha3, alpha2]) => {
    acc[alpha2] = alpha3;
    return acc;
  },
  {} as Record<string, string>
);

// ISO 4217 currency codes by country
const COUNTRY_CURRENCIES: Record<string, string> = {
  // Primary Markets
  US: 'USD',
  TR: 'TRY',

  // Eurozone
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  GR: 'EUR',
  PT: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  SK: 'EUR',

  // Non-Euro EU
  PL: 'PLN',
  RO: 'RON',
  BG: 'BGN',
  HU: 'HUF',
  CZ: 'CZK',
  SE: 'SEK',
  DK: 'DKK',

  // Other
  GB: 'GBP',
  CH: 'CHF',
  NO: 'NOK',
  CA: 'CAD',
  MX: 'MXN',
};

/**
 * Get country name from ISO 3166-1 alpha-2 country code
 * @param countryCode - Two-letter country code (e.g., 'US', 'TR', 'DE')
 * @returns Country name or the code itself if not found
 */
export function getCountryName(countryCode: string): string {
  if (!countryCode) return '';
  const upperCode = countryCode.toUpperCase();
  return COUNTRY_NAMES[upperCode] ?? upperCode;
}

/**
 * Get ISO 3166-1 alpha-2 country code from country name or code
 * Handles: alpha-2 ('US'), alpha-3 ('USA'), or full name ('United States')
 * @param countryName - Country code or name
 * @returns Two-letter country code or 'US' as default (USA-first)
 */
export function getCountryCode(countryName: string): string {
  if (!countryName) return 'US'; // Default to US (USA-first)

  const upperInput = countryName.toUpperCase();

  // If already a 2-letter code, validate and return
  if (countryName.length === 2) {
    if (COUNTRY_NAMES[upperInput]) {
      return upperInput;
    }
  }

  // Check for 3-letter code (ISO alpha-3, e.g., 'USA', 'TUR')
  if (countryName.length === 3) {
    const alpha2 = ALPHA3_TO_ALPHA2[upperInput];
    if (alpha2) {
      return alpha2;
    }
  }

  // Lookup by name (case-insensitive)
  const code = NAME_TO_CODE[countryName.toLowerCase()];
  return code ?? 'US'; // Default to US if not found
}

/**
 * Get currency code from country code
 * @param countryCode - Two-letter country code
 * @returns ISO 4217 currency code (e.g., 'USD', 'TRY', 'EUR')
 */
export function getCountryCurrency(countryCode: string): string {
  if (!countryCode) return 'USD'; // Default to USD (USA-first)
  const upperCode = countryCode.toUpperCase();
  return COUNTRY_CURRENCIES[upperCode] ?? 'USD';
}

/**
 * Check if a country code is valid (exists in our mapping)
 * @param countryCode - Two-letter country code
 * @returns true if valid
 */
export function isValidCountryCode(countryCode: string): boolean {
  if (!countryCode) return false;
  return countryCode.toUpperCase() in COUNTRY_NAMES;
}

/**
 * Get all supported country codes
 * @returns Array of country codes
 */
export function getSupportedCountryCodes(): string[] {
  return Object.keys(COUNTRY_NAMES);
}

/**
 * Get all supported countries as options (for dropdowns)
 * @param locale - Optional locale for translated names (default: 'en')
 * @returns Array of { code, name } objects
 */
export function getCountryOptions(locale?: string): Array<{ code: string; name: string }> {
  const names = locale === 'tr' ? COUNTRY_NAMES_TR : COUNTRY_NAMES;
  return Object.entries(COUNTRY_NAMES).map(([code]) => ({
    code,
    name: names[code] ?? COUNTRY_NAMES[code] ?? code,
  }));
}

/**
 * Get ISO 3166-1 alpha-3 country code from alpha-2 code
 * Required for HERE Maps Autosuggest API which uses alpha-3 codes
 * @param alpha2Code - Two-letter country code (e.g., 'US', 'TR', 'DE')
 * @returns Three-letter country code (e.g., 'USA', 'TUR', 'DEU') or original code if not found
 */
export function getCountryCodeAlpha3(alpha2Code: string): string {
  if (!alpha2Code) return 'USA'; // Default to USA (USA-first)
  const upperCode = alpha2Code.toUpperCase();
  return ALPHA2_TO_ALPHA3[upperCode] ?? upperCode;
}
